// 📄 components/Configuracion/ConfiguracionInterfacesZ.tsx
// Gestión de las interfaces/canales Z (desarrollos SAP personalizados)
// propios de ESTE proyecto — mismo patrón estructural que
// ConfiguracionTransaccionesZ.tsx / ConfiguracionBadisZ.tsx (lista + modal
// alta/edición + ConfirmDialog para borrar), guardado DIRECTO contra
// Firestore (ver interfazZService.ts).
import { useEffect, useState } from "react";
import { ArrowLeftRight, Plus, RefreshCw, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext.tsx";
import {
  getInterfacesZ, eliminarInterfazZ, InterfazZ
} from "../../services/interfazZService.ts";
import { objetoNegocioMeta, solucionInterfazMeta, plataformaInterfazMeta, direccionInterfazMeta } from "../../services/catalogoInterfacesService.ts";
import ConfirmDialog from "../ui/ConfirmDialog.tsx";
import Alert from "../ui/Alert.tsx";
import InterfazZFormModal from "./InterfazZFormModal.tsx";

interface ConfiguracionInterfacesZProps {
  projectId: string;
  projectNombre?: string;
}

export default function ConfiguracionInterfacesZ({ projectId, projectNombre }: ConfiguracionInterfacesZProps) {
  // Borrar está restringido a Admin en firestore.rules (mismo patrón que
  // transaccionesZ) — se oculta el botón para el resto de los miembros.
  const { profile } = useAuth();
  const esAdmin = profile?.role === "admin";
  const [interfaces, setInterfaces] = useState<InterfazZ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<InterfazZ | null>(null);
  const [aEliminar, setAEliminar] = useState<InterfazZ | null>(null);
  const [eliminando, setEliminando] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const showAlert = (msg: string, type: "success" | "error" | "warning" = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 4000);
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const data = await getInterfacesZ(projectId);
      setInterfaces(data);
    } catch (err) {
      console.error("Error cargando las interfaces Z:", err);
      setError("No se pudieron cargar las interfaces Z de este proyecto.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleGuardado = () => {
    const eraEdicion = !!editando;
    setModalOpen(false);
    setEditando(null);
    cargar();
    showAlert(eraEdicion ? "Interfaz Z actualizada correctamente." : "Interfaz Z guardada correctamente.");
  };

  const handleCancelarModal = () => {
    setModalOpen(false);
    setEditando(null);
  };

  const handleConfirmarEliminar = async () => {
    if (!aEliminar || eliminando) return;
    setEliminando(true);
    try {
      await eliminarInterfazZ(projectId, aEliminar.id);
      setAEliminar(null);
      cargar();
      showAlert("Interfaz Z eliminada correctamente.");
    } catch (err: any) {
      showAlert(err.message || "Error al eliminar la interfaz Z.", "error");
    } finally {
      setEliminando(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando interfaces Z...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Alert message={alertMessage} type={alertType} onClose={() => setAlertMessage("")} />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-300">Interfaces / Canales Z</h3>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-1.5 text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Nueva interfaz Z
        </button>
      </div>

      {interfaces.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <ArrowLeftRight className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Este proyecto todavía no tiene interfaces/canales Z propios.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {interfaces.map((i) => {
            const objMeta = objetoNegocioMeta(i.objetoNegocio);
            const solMeta = solucionInterfazMeta(i.solucion);
            const platMeta = plataformaInterfazMeta(i.plataforma);
            const dirMeta = direccionInterfazMeta(i.direccion);
            return (
              <div key={i.id} className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-mono font-semibold text-white truncate">{i.nombreTecnico}</span>
                    <span className="text-xs text-gray-400 truncate">{i.textoBreve}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">
                      Z
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      objMeta ? 'text-blue-300 bg-blue-500/10 border-blue-500/30' : 'text-gray-400 bg-white/5 border-white/20'
                    }`}>
                      {objMeta ? objMeta.label : 'Sin clasificar'}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                      {solMeta.label}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-sky-300 bg-sky-500/10 border-sky-500/30">
                      {platMeta.label}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-purple-300 bg-purple-500/10 border-purple-500/30">
                      {dirMeta.labelCorta}
                    </span>
                    {i.sistemasDisponibles && i.sistemasDisponibles.map((s) => (
                      <span key={s} className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border text-sky-300 bg-sky-500/10 border-sky-500/30">
                        {s}
                      </span>
                    ))}
                  </div>
                  {i.responsable && (
                    <p className="text-[11px] text-gray-500 mt-1.5">Responsable: <span className="text-gray-300">{i.responsable}</span></p>
                  )}
                </div>
                <button
                  onClick={() => setEditando(i)}
                  className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
                  title="Editar"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {esAdmin && (
                  <button
                    onClick={() => setAEliminar(i)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-all flex-shrink-0"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(modalOpen || editando) && (
        <InterfazZFormModal
          projectId={projectId}
          interfazInicial={editando}
          onGuardado={handleGuardado}
          onCancelar={handleCancelarModal}
        />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta interfaz Z?"
          message={`"${aEliminar.nombreTecnico}" se eliminará de ${projectNombre || "este proyecto"}. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? "Eliminando..." : "Eliminar"}
          danger
          onConfirm={handleConfirmarEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
